/**
 * @title Profile page - input form
 * @description Editing the consumer background information
 * @image https://storageaccountourimbea2.blob.core.windows.net/tradein2guide/profile.png
 * @image https://storageaccountourimbea2.blob.core.windows.net/tradein2guide/profile-terms.png
 */
import * as React from "react";
import {useCallback, useEffect} from "react";
import {FormProvider, useForm} from "react-hook-form";
import {ConsumerCarriersDocument, useConsumerConvertCurrencyLazyQuery, useConsumerFormQuery, useConsumerFormSaveMutation} from "../../../Queries";
import {Box} from "@mui/material";
import {FormInputCountry} from "../../../components/FormInputCountry";
import {FormInputText} from "../../../components/FormInputText";
import {FormInputSelect, SelectOption} from "../../../components/FormInputSelect";
import {isValidEmail} from "../../../components/Validations";
import {useRecoilState, useRecoilValue} from "recoil";
import {StyledDiv} from "../../../providers/layout/LayoutProvider";
import {PubSub} from "../../../utility/PubSub";
import {LocaleAtom, LocalizationsAtom} from "../../../providers/localization/LocalizationProvider";
import {Languages} from "../../../data/Languages";
import {AccessContextData} from "../../../providers/access/AppContextProvider";
import {Countries} from "./Countries";
import parsePhoneNumber, {CountryCode} from 'libphonenumber-js'
import {Analytics, AnalyticsEvent} from "../../../analytics/Analytics";
import {AppDataAtom} from "../../../providers/access/ApplicationDataProvider";
import {FormInputCheckbox} from "../../../components/FormInputCheckbox";
import {ConsumerFormData} from "./ConsumerType";
import {ReturnsSum} from "../returns/ReturnsCart";
import {TermsAndConditionsDialog} from "../../../components/TermsAndConditionsDialog";

export type ConsumerFormProps = {
   shopId: string
   consumerId: string
}

const phoneRegExp = /^[\d ]+$/
const zipCodeRegExp = /^\d{4,5}$/

export const ConsumerForm = (props: ConsumerFormProps) => {
   const [appContext, setAppContext] = useRecoilState(AccessContextData)
   const appData = useRecoilValue(AppDataAtom)
   const language = useRecoilValue(LocaleAtom);
   const localizations = useRecoilValue(LocalizationsAtom);
   const tradeInTotal = useRecoilValue(ReturnsSum);
   const isHidden = !!window.useExternalCustomerData;

   const [convertCurrency] = useConsumerConvertCurrencyLazyQuery()

   const [locale, setLocale] = useRecoilState(LocaleAtom);
   const {data} = useConsumerFormQuery({
      variables: {
         shopId: props.shopId,
         consumerId: props.consumerId,
      }
   });
   const [terms, setTerms] = React.useState<boolean>(false);
   const [animation, setAnimation] = React.useState<boolean>(false);
   const [save] = useConsumerFormSaveMutation()

   const methods = useForm<ConsumerFormData>({
      mode: "onChange",
      resolver: isHidden ? undefined : (values, context) => {
         const errors: any = {}
         if (!values.name) {
            errors.name = {
               type: 'validation',
               message: localizations.profileRequired
            }
         }
         if (!values.mail) {
            errors.mail = {
               type: 'validation',
               message: localizations.profileRequired
            }
         } else if (!isValidEmail(values.mail, appData.invalidEmailReg)) {
            errors.mail = {
               type: 'validation',
               message: localizations.profileMailInvalid
            }
         }
         if (!values.phone) {
            errors.phone = {
               type: 'validation',
               message: localizations.profileRequired
            }
         } else if (!values.phonePrefix) {
            errors.phonePrefix = {
               type: 'validation',
               message: localizations.profileRequired
            }
         } else if (!values.phone.match(phoneRegExp)) {
            errors.phone = {
               type: 'validation',
               message: localizations.profilePhoneValidation
            }
         }
         if (!values.zipCode) {
            errors.zipCode = {
               type: 'validation',
               message: localizations.profileRequired
            }
         } else {
            const country = Countries.find(country => country.ISO === values.countryCode)
            if (country?.Regex) {
               if (!values.zipCode.match(country.Regex)) {
                  errors.zipCode = {
                     type: 'validation',
                     message: localizations.profileZipCodeValidation + ', format: ' + country.Format
                  }
               }
            } else if (!values.zipCode.match(zipCodeRegExp)) {
               errors.zipCode = {
                  type: 'validation',
                  message: localizations.profileZipCodeValidation
               }
            }
         }
         if (!values.countryCode) {
            errors.countryCode = {
               type: 'validation',
               message: localizations.profileRequired
            }
         }
         if (!values.acceptedTerms) {
            errors.countryCode = {
               type: 'validation',
               message: localizations.profileRequired
            }
         }

         return {
            values,
            errors: errors
         }
      },
      defaultValues: {
         name: '',
         zipCode: '',
         city: '',
         countryCode: '',
         mail: '',
         phone: '',
         phonePrefix: '',
         language: '',
         acceptPart2: false,
         acceptPart3: false,
         address: ''
      }
   });
   const {handleSubmit, trigger, setValue, getValues, reset, watch, control, formState: {errors, isValid}} = methods;

   React.useEffect(() => {
      if (!isHidden) {
         const subscription = watch((value, {name}) => {
            if (!name?.startsWith('accept')) {
               resetAccept()
            }
         })
         return () => subscription.unsubscribe()
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [isHidden])

   const onSubmit = useCallback(async (consumer: ConsumerFormData) => {
      if (consumer.language && consumer.language !== locale) {
         setLocale(consumer.language);
      }

      const {data} = await save({
         variables: {
            consumer: {
               _id: props.consumerId,
               shopId: props.shopId,
               name: consumer.name,
               zipCode: consumer.zipCode,
               city: consumer.city,
               countryCode: consumer.countryCode || appData.countries?.[0]?.code,
               phone: '+' + consumer.phonePrefix + ' ' + consumer.phone,
               mail: consumer.mail?.toLowerCase(),
               locale: consumer.language || 'en',
               externalId: consumer.externalId,
               add1: consumer.address
            }
         },
         refetchQueries: [{
            query: ConsumerCarriersDocument,
            variables: {
               shopId: props.shopId,
               consumerId: props.consumerId
            }
         }]
      });
      if (!data?.ConsumerUpdate?._id) {
         throw new Error('Unable to save')
      }

      if (data?.ConsumerUpdate?.currency && data?.ConsumerUpdate?.currency !== appContext.currency) {
         setAppContext({
            ...appContext,
            currency: data.ConsumerUpdate.currency
         });
      }
   }, [appContext, appData.countries, locale, props.consumerId, props.shopId, save, setAppContext, setLocale]);

   const resetAccept = () => {
      if (!isHidden) {
         setValue('acceptedTerms', false)
      }
   }

   useEffect(() => {
      if (data) {
         const countryCode = data.ConsumerById!.countryCode as CountryCode || "DK";
         const phoneNumber = parsePhoneNumber(data.ConsumerById!.phone || '1', countryCode)
         const defaultPrefix = Countries.find(country => country.ISO === countryCode)?.PhonePrefix

         reset({
            phone: phoneNumber?.nationalNumber || '',
            phonePrefix: phoneNumber?.countryCallingCode || defaultPrefix,
            language: data.ConsumerById?.language,
            name: data.ConsumerById?.name,
            countryCode: data.ConsumerById?.countryCode,
            zipCode: data.ConsumerById?.zipCode,
            city: data.ConsumerById?.city,
            mail: data.ConsumerById?.mail,
            address: data.ConsumerById?.add1
         })
      }
   }, [data, reset]);

   useEffect(() => {
      if (!isValid) {
         resetAccept()
      }
      // eslint-disable-next-line
   }, [isValid])

   useEffect(() => {
      return PubSub.on('navigation-blocked', () => {
         setAnimation(true);

         setTimeout(() => {
            setAnimation(false);
         }, 1500)
      })
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [])

   useEffect(() => {
      return PubSub.on('consumerform-valid', async () => {
         const formValid = await trigger();
         const accept2 = localizations.profileAcceptPart2 ? getValues('acceptPart2') : true
         const accept3 = localizations.profileAcceptPart3 ? getValues('acceptPart3') : true
         if (formValid && accept2 && accept3) {
            await handleSubmit(onSubmit)()
            return true;
         } else {
            return false;
         }
      })
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [isValid])

   const onTermsClose = () => {
      setTerms(false);
   }

   const options: SelectOption[] = data?.ShopById?.tradeInLocales?.map(locale => {
      const language = Languages.get(locale)
      return {
         id: language.code,
         name: language.native
      }
   }) || []

   useEffect(() => {
      const handleAuthComplete = (event: any) => {
         let newData: ConsumerFormData = {
            name: '',
            zipCode: '',
            city: '',
            countryCode: '',
            mail: '',
            phone: '',
            phonePrefix: '',
            language: language || '',
            acceptPart2: false,
            acceptPart3: false,
            address: '',
            ...event.detail,
            acceptedTerms: true
         };
         reset(newData, {
            keepErrors: false
         });
         (async function () {
            console.log('Submitting form', {
               event: event.detail,
               data: newData
            });
            await handleSubmit(onSubmit)();
            PubSub.emit('navigation-forward');
         })();
      };
      window.addEventListener('tradeInApp:updateCustomerData', handleAuthComplete);
      return () => {
         window.removeEventListener('tradeInApp:updateCustomerData', handleAuthComplete);
      };
   }, [handleSubmit, language, onSubmit, reset]);

   useEffect(() => {
      const onNavigate = (event: any) => {
         // Wait 200 ms, then fire the event
         setTimeout(() => {
            PubSub.emit('navigation-backward');
         }, 100);
      }
      window.addEventListener('tradeInApp:navigateBack', onNavigate);
      return () => {
         window.removeEventListener('tradeInApp:navigateBack', onNavigate);
      };
   }, []);


   useEffect(() => {
      if (isHidden) {
         (async function () {
            const {data} = await convertCurrency({
               variables: {
                  consumerId: props.consumerId,
                  inputCurrency: appContext.shopCurrency!,
                  inputValue: tradeInTotal
               }
            });
            const event = new CustomEvent('tradeInApp:requestCustomerData', {
               detail: {
                  tradeInTotal: Math.round(data?.ConvertCurrencyForConsumer?.value || 0)
               }
            });
            window.dispatchEvent(event);
         })();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [isHidden, tradeInTotal]);

   //DO NOT REMOVE - used for localizations
   //localizations.profileCountry_dk
   //localizations.profileCountry_at
   //localizations.profileCountry_de

   const showCountrySelector = (appData.countries.length > 1) || !data?.ConsumerById?.countryCode;
   return <FormProvider {...methods}>
      <StyledDiv type={"main"} sx={{display: isHidden ? 'none' : 'initial' }}>
         {(Object.keys(errors || {}).length > 0 && !isValid) && <Box sx={{textAlign: 'center'}}>
            <StyledDiv sx={{color: 'rgb(240,0,0)'}} type={"main"}>{localizations.profileDataMissingTitle}</StyledDiv>
         </Box>}
         {showCountrySelector &&
            <Box sx={{marginBottom: "8px"}}>
               <FormInputCountry countries={appData.countries.map(country => country.code)}
                                 name="countryCode"
                                 control={control}
                                 onChange={() => resetAccept()}
                                 label={localizations.profileCountry}
                                 errorMessage={localizations.profileDataMissingTitle}/>
            </Box>
         }
         <Box sx={{display: 'flex', gap: '5px'}}>
            <Box sx={{flex: '0 0 50%'}}>
               <FormInputText name="name" label={localizations.profileName}
                              errorMessage={localizations.profileDataMissingTitle}/>
            </Box>
            <Box sx={{flex: '1'}}>
               <FormInputText name="mail" label={localizations.profileMail}
                              disabled={appContext.lockEmail || false}
                              errorMessage={localizations.profileDataMissingTitle}/>
            </Box>
         </Box>
         <FormInputText handleError={true} name="address" label={localizations.profileAddress}/>
         <Box sx={{display: 'flex', gap: '5px'}}>
            <Box sx={{flex: '0 0 30%'}}>
               <FormInputText handleError={false} name="zipCode" label={localizations.profileZipCode}/>
            </Box>
            <Box sx={{flex: '1'}}>
               <FormInputText handleError={true} name="city" label={localizations.profileCity}/>
            </Box>
         </Box>
         {errors?.["zipCode"]?.message &&
            <StyledDiv sx={{color: 'rgb(240,0,0)', marginTop: '-8px', marginBottom: '8px'}} type={"main"}>
               <span style={{fontSize: '90%'}}>
                  {errors?.["zipCode"]?.message && errors?.["zipCode"]?.message}
               </span>
            </StyledDiv>
         }
         {(options.length > 1 && !appData.hideLanguageSelector) && <FormInputSelect
            options={options}
            name="language"
            onSelectChange={() => resetAccept()}
            label={localizations.profileLocale}
            errorMessage={localizations.profileDataMissingTitle}/>
         }
         <Box sx={{display: 'flex', gap: '5px'}}>
            <FormInputSelect name={"phonePrefix"} label={localizations.profilePhonePrefix}
                             options={Countries.map(country => ({
                                id: country.PhonePrefix,
                                name: country.Country + ' +' + country.PhonePrefix,
                                label: '+' + country.PhonePrefix
                             }))}
                             sx={{
                                width: '140px'
                             }}
            />
            <FormInputText name="phone" label={localizations.profilePhone}
                           errorMessage={localizations.profilePhoneValidation}/>
         </Box>

         <StyledDiv type={"main"} style={{
            display: 'flex',
            flexDirection: 'column',
            rowGap: '8px', alignItems: 'top', cursor: 'pointer',
            padding: '5px',
            border: animation ? '2px solid rgb(211, 47, 47)' : '2px solid transparent', borderRadius: '4px'
         }}>
            {localizations.profileAcceptPart2 &&
               <FormInputCheckbox name={"acceptPart2"} label={localizations.profileAcceptPart2}/>
            }

            {localizations.profileAcceptPart3 &&
               <FormInputCheckbox name={"acceptPart3"} label={localizations.profileAcceptPart3}/>
            }

            <FormInputCheckbox name={"acceptedTerms"}
                               onTextClick={(text) => {
                                  if (text === '*') {
                                     Analytics.event(AnalyticsEvent.trackTermsRead);
                                     setTerms(true);
                                     return true;
                                  } else {
                                     return false;
                                  }
                               }}
                               label={localizations.profileAcceptsTerms + (localizations.profileAcceptsTerms2 ? ' {{' + localizations.profileAcceptsTerms2 + '::*}}' : '')}
            />
            <FormInputText name={"externalId"} label={"Ignore"} hidden={true} sx={{display: 'none'}}/>
         </StyledDiv>

         {terms && <TermsAndConditionsDialog
            open={terms}
            onHideDialog={onTermsClose}
         />}

      </StyledDiv>
   </FormProvider>
}